import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ExternalLink } from "lucide-react";
import {
  SummaryCards,
  DimensionAverages,
  ReviewsList,
  PeriodSelector,
  useReviewsReport,
  useToggleReviewHidden,
  usePedidoDetail,
} from "../features/relatorio-avaliacoes";
import { PedidoDetailDialog } from "../features/pedidos/components/PedidoDetailDialog";
import { Button } from "../components/ui/button";
import TablePagination from "../components/TablePagination";
import { CarregandoBloco, CarregandoCards, CarregandoLista } from "../components/carregando";

const PAGE_SIZE_DEFAULT = 10;

/**
 * Página de Relatório de Avaliações (magra). Paginação client-side 10/página,
 * ordem das dimensões respeita a config da campanha (vem do backend), e o
 * "Ver pedido" abre o popup COMPLETO de Vendas → Pedidos em modo read-only
 * (botões de cancelar/reimprimir omitidos via callbacks ausentes).
 */
export default function RelatorioAvaliacoes() {
  const navigate = useNavigate();
  const [days, setDays] = useState(90);
  const [selectedPedidoId, setSelectedPedidoId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(PAGE_SIZE_DEFAULT);

  const { data, loading, refresh, applyLocal } = useReviewsReport(days);
  const { toggle: toggleHidden, pending: hiddenPending } = useToggleReviewHidden({
    applyLocal,
    refresh,
  });

  // Reset pra página 1 quando muda período (ou a lista total muda muito).
  useEffect(() => setCurrentPage(1), [days]);

  // Carregamento com cards (25/10/2026): esqueleto só até a 1ª resposta. Antes
  // a TELA INTEIRA virava um círculo girando também ao trocar o período e ao
  // ocultar uma avaliação da média (o recálculo recarrega o relatório).
  const [primeiraCarga, setPrimeiraCarga] = useState(true);
  useEffect(() => { if (!loading) setPrimeiraCarga(false); }, [loading]);

  const reviews = useMemo(() => data?.reviews || [], [data]);
  const totalCount = reviews.length;
  const pagedReviews = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return reviews.slice(start, start + itemsPerPage);
  }, [reviews, currentPage, itemsPerPage]);

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      <Header days={days} setDays={setDays} onRefresh={refresh} />
      {primeiraCarga ? (
        <>
          <CarregandoCards quantos={3} className="grid grid-cols-1 md:grid-cols-3 gap-4" altura="h-[172px]" />
          <CarregandoBloco altura="h-[150px]" />
          <CarregandoLista linhas={5} colunas={1} />
        </>
      ) : (
      <div className={`space-y-6 transition-opacity duration-200 ${loading ? "opacity-50 pointer-events-none" : ""}`}>
      <SummaryCards
        avgGeral={data?.avg_geral}
        total={data?.total || 0}
        sent={data?.sent || 0}
        dist={data?.distribuicao || {}}
      />
      <DimensionAverages
        dimensions={data?.dimensions || []}
        medias={data?.medias_por_dimensao || {}}
      />
      <ReviewsList
        reviews={pagedReviews}
        totalCount={totalCount}
        dimensions={data?.dimensions || []}
        onOpenPedido={setSelectedPedidoId}
        onToggleHidden={toggleHidden}
        hiddenPending={hiddenPending}
      />
      {totalCount > itemsPerPage && (
        <TablePagination
          currentPage={currentPage}
          totalItems={totalCount}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(n) => {
            setItemsPerPage(n);
            setCurrentPage(1);
          }}
        />
      )}
      </div>
      )}
      <AvaliacaoPedidoDialog
        pedidoId={selectedPedidoId}
        dimensions={data?.dimensions || []}
        onClose={() => setSelectedPedidoId(null)}
        onOpenFull={() => {
          const id = selectedPedidoId;
          setSelectedPedidoId(null);
          navigate(`/admin/vendas/pedidos?openPedido=${id}`);
        }}
      />
    </div>
  );
}

/**
 * Wrapper que faz o fetch do pedido + renderiza o PedidoDetailDialog completo
 * com actions read-only. Inclui um botão extra no footer "Abrir em Pedidos"
 * pra quando o operador precisar agir (cancelar, reimprimir).
 */
function AvaliacaoPedidoDialog({ pedidoId, dimensions, onClose, onOpenFull }) {
  const { pedido, loading } = usePedidoDetail(pedidoId);
  const open = !!pedidoId;
  if (loading && open) {
    return (
      <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={onClose}>
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }
  return (
    <PedidoDetailDialog
      open={open && !!pedido}
      pedido={pedido}
      onClose={onClose}
      catalog={{ productsMap: {}, dimensions }}
      actions={{
        extraFooterButtons: (
          <Button variant="default" size="sm" className="flex-1 h-9 text-xs" onClick={onOpenFull}>
            Abrir em Pedidos <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        ),
      }}
    />
  );
}

function Header({ days, setDays, onRefresh }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
      <div>
        <h1 className="text-3xl font-bold">Relatório de Avaliações</h1>
        <p className="text-muted-foreground mt-1">
          Notas e comentários dos seus clientes nos últimos {days} dias
        </p>
      </div>
      <PeriodSelector days={days} setDays={setDays} onRefresh={onRefresh} />
    </div>
  );
}
